import { getData, updateData } from './firebase/api-wrapper.service';
import logger from '../utils/logger';

export interface SchoolLocation {
  placeID: string;
  name: string;
  address: string;
  locality?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  // Deprecated fields for backward compatibility
  lat?: number;
  lng?: number;
  countryISOAlpha2?: string;
  instructor?: string;
}

// Alternative interface for backward compatibility (maps to SchoolLocation)
export interface LocationData {
  placeID: string; // Standardized to match original app
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  country?: string;
  city?: string;
  state?: string;
  formattedAddress?: string;
  // Deprecated field for backward compatibility
  placeId?: string;
}

const RECENT_LOCATIONS_KEY = 'recentLocations_v2';
const MAX_RECENT_LOCATIONS = 10;

class LocationService {
  // Get recent locations from localStorage
  getRecentLocations(): SchoolLocation[] {
    try {
      const stored = localStorage.getItem(RECENT_LOCATIONS_KEY);
      if (!stored) return [];
      
      const locations = JSON.parse(stored);
      return Array.isArray(locations) ? locations : [];
    } catch (error) {
      logger.error('Error loading recent locations:', error);
      return [];
    }
  }

  // Add a location to recent locations
  addRecentLocation(location: SchoolLocation): void {
    try {
      const recent = this.getRecentLocations();
      
      // Remove if already exists (to move to front)
      const filtered = recent.filter(loc => loc.placeID !== location.placeID);
      
      // Add to front
      const updated = [location, ...filtered].slice(0, MAX_RECENT_LOCATIONS);
      
      localStorage.setItem(RECENT_LOCATIONS_KEY, JSON.stringify(updated));
    } catch (error) {
      logger.error('Error saving recent location:', error);
    }
  }

  // Store location in Firebase (for shared access)
  async saveLocationToFirebase(location: SchoolLocation): Promise<void> {
    try {
      if (!location.placeID) return;
      
      logger.info('LocationService: About to save location using api-wrapper:', {
        placeID: location.placeID,
        name: location.name
      });
      
      await updateData(`/locations/${location.placeID}`, {
        ...location,
        lastUpdated: Date.now()
      });
      
      logger.info('LocationService: Successfully saved location to Firebase');
    } catch (error) {
      logger.error('LocationService: Error saving location to Firebase:', error);
      throw error; // Re-throw to see the full error
    }
  }

  // Get location from Firebase
  async getLocationFromFirebase(placeID: string): Promise<SchoolLocation | null> {
    try {
      const data = await getData(`/locations/${placeID}`);
      return data as SchoolLocation | null;
    } catch (error) {
      logger.error('Error fetching location from Firebase:', error);
      return null;
    }
  }

  // Search locations by name or address
  searchRecentLocations(query: string): SchoolLocation[] {
    const recent = this.getRecentLocations();
    const lowerQuery = query.toLowerCase();
    
    return recent.filter(location =>
      location.name.toLowerCase().includes(lowerQuery) ||
      location.address.toLowerCase().includes(lowerQuery) ||
      (location.locality && location.locality.toLowerCase().includes(lowerQuery))
    );
  }

  // Clear all recent locations
  clearRecentLocations(): void {
    try {
      localStorage.removeItem(RECENT_LOCATIONS_KEY);
    } catch (error) {
      logger.error('Error clearing recent locations:', error);
    }
  }

  // Functions migrated from firebase/location.service.ts for analytics and display

  /**
   * Retrieves a specific location's data using its Place ID
   * @param placeId - Google Place ID for the location
   */
  async getLocationData(placeId: string): Promise<LocationData> {
    try {
      const data = await getData(`/locations/${placeId}`);
      
      if (data) {
        return data as LocationData;
      }
      
      // Return a placeholder if location metadata doesn't exist
      logger.warn(`Location metadata not found for ID: ${placeId}`);
      return {
        placeID: placeId, // Standardized field name
        placeId, // Backward compatibility
        name: 'Unknown Location',
        address: '',
        latitude: 0,
        longitude: 0
      };
    } catch (error: any) {
      logger.error('Error fetching location data:', error);
      // Return a placeholder on error
      return {
        placeID: placeId, // Standardized field name
        placeId, // Backward compatibility
        name: 'Unknown Location',
        address: '',
        latitude: 0,
        longitude: 0
      };
    }
  }

  /**
   * Retrieves multiple locations' data using an array of Place IDs
   * @param placeIds - Array of Google Place IDs
   */
  async getLocationsData(placeIds: string[]): Promise<LocationData[]> {
    try {
      const promises = placeIds.map(placeId => this.getLocationData(placeId));
      return await Promise.all(promises);
    } catch (error: any) {
      logger.error('Error fetching multiple locations data:', error);
      throw error;
    }
  }

  /**
   * Parse coordinates from legacy coord_* placeID format
   * Format: coord_42_346513_-71_553521 = lat 42.346513, lng -71.553521
   * @param placeID - The placeID string to parse
   * @returns {latitude, longitude} or null if not a coord_ format
   */
  parseCoordPlaceID(placeID: string): { latitude: number; longitude: number } | null {
    if (!placeID.startsWith('coord_')) {
      return null;
    }

    try {
      // Remove 'coord_' prefix and split by underscore
      const parts = placeID.substring(6).split('_');

      // Format: lat_latDecimal_lng_lngDecimal
      // Example: 42_346513_-71_553521 → lat=42.346513, lng=-71.553521
      if (parts.length >= 4) {
        const latWhole = parseInt(parts[0], 10);
        const latDecimal = parts[1];
        const lngWhole = parseInt(parts[2], 10);
        const lngDecimal = parts[3];

        const latitude = parseFloat(`${latWhole}.${latDecimal}`);
        const longitude = parseFloat(`${lngWhole}.${lngDecimal}`);

        if (!isNaN(latitude) && !isNaN(longitude)) {
          return { latitude, longitude };
        }
      }
    } catch (error) {
      logger.warn(`Failed to parse coord placeID: ${placeID}`, error);
    }

    return null;
  }

  /**
   * Gets all unique locations where a user has trained
   * @param userId - Firebase user ID
   */
  async getTrainingLocations(userId: string): Promise<LocationData[]> {
    try {
      // Use original app's data path - /stats/{userId}/public/locations
      const locationStats = await getData(`/stats/${userId}/public/locations`) || {};

      // Process location statistics (format: { [placeID]: { checkIns: number, ... } })
      const placeIDs = Object.keys(locationStats);

      if (placeIDs.length === 0) {
        logger.info(`No training locations found in stats for user: ${userId}`);
        return [];
      }

      // Get detailed location data for each placeID from /locations/{placeID}
      const locationPromises = placeIDs.map(async (placeID) => {
        try {
          const locationData = await getData(`/locations/${placeID}`);
          if (locationData) {
            return {
              placeID,
              name: locationData.name || 'Unknown Location',
              address: locationData.address || '',
              latitude: locationData.latitude || locationData.lat || 0,
              longitude: locationData.longitude || locationData.lng || 0,
              country: locationData.country,
              city: locationData.locality || locationData.city,
              formattedAddress: locationData.address || locationData.formattedAddress,
              // Include training frequency from stats
              checkIns: locationStats[placeID].checkIns || 0
            } as LocationData & { checkIns: number };
          }

          // Fallback: Handle legacy coord_* placeIDs that have no metadata in /locations
          // These have coordinates encoded in the placeID itself
          const parsedCoords = this.parseCoordPlaceID(placeID);
          if (parsedCoords) {
            logger.info(`Parsed coordinates from legacy placeID: ${placeID}`);
            return {
              placeID,
              name: 'Training Location',
              address: '',
              latitude: parsedCoords.latitude,
              longitude: parsedCoords.longitude,
              checkIns: locationStats[placeID].checkIns || 0
            } as LocationData & { checkIns: number };
          }

          return null;
        } catch (error) {
          logger.warn(`Failed to fetch location data for placeID: ${placeID}`, error);
          return null;
        }
      });

      const locationResults = await Promise.all(locationPromises);
      const validLocations = locationResults.filter(Boolean) as (LocationData & { checkIns: number })[];

      // Sort by check-ins (most visited first) like the original app
      const sortedLocations = validLocations
        .sort((a, b) => (b.checkIns || 0) - (a.checkIns || 0))
        .map(({ checkIns, ...location }) => location); // Remove checkIns from final result

      logger.info(`Found ${sortedLocations.length} unique training locations for user: ${userId}`);
      return sortedLocations;
    } catch (error: any) {
      logger.error('Error fetching training locations from user stats:', error);

      // Enhanced error logging for debugging
      if (error?.code === 'PERMISSION_DENIED' || error?.message?.includes('Permission denied')) {
        logger.error(`Permission denied accessing /stats/${userId}/public/locations. Check authentication state.`);
      }

      // Fallback: if stats access fails, show empty (original app behavior)
      return [];
    }
  }

  /**
   * Gets a list of countries where the user has trained
   * @param userId - Firebase user ID
   */
  async getUniqueTrainingCountries(userId: string): Promise<string[]> {
    try {
      const locations = await this.getTrainingLocations(userId);
      const countries = locations
        .map(location => location.country)
        .filter((country): country is string => !!country);
      
      return [...new Set(countries)];
    } catch (error: any) {
      logger.error('Error fetching unique training countries:', error);
      return []; // Return empty array instead of throwing (original app behavior)
    }
  }

  /**
   * Gets ALL countries from trainings, including those without coordinates
   * @param userId - Firebase user ID
   */
  async getAllTrainingCountries(userId: string): Promise<string[]> {
    try {
      // Get user's trainings data using proper Firebase query to satisfy permission rules
      const { getDataByQuery } = await import('./firebase/api-wrapper.service');
      const trainingsData = await getDataByQuery('/trainings', {
        orderByChild: 'owner',
        equalTo: userId
      });
      
      const countries = new Set<string>();
      
      // Extract all countries from trainings for this user
      trainingsData.forEach((training: any) => {
        
        if (training.location && training.location.country) {
          countries.add(training.location.country);
        }
      });
      
      const uniqueCountries = [...countries].sort();
      logger.info(`Found ${uniqueCountries.length} unique countries from all trainings for user ${userId}`);
      return uniqueCountries;
    } catch (error: any) {
      logger.error('Error fetching all training countries:', error);
      throw error;
    }
  }
}

export const locationService = new LocationService();
export default locationService;

// Export individual functions for backward compatibility with firebase/location.service.ts
export const getLocationData = (placeId: string): Promise<LocationData> => 
  locationService.getLocationData(placeId);

export const getLocationsData = (placeIds: string[]): Promise<LocationData[]> => 
  locationService.getLocationsData(placeIds);

export const getTrainingLocations = (userId: string): Promise<LocationData[]> => 
  locationService.getTrainingLocations(userId);

export const getUniqueTrainingCountries = (userId: string): Promise<string[]> => 
  locationService.getUniqueTrainingCountries(userId);

export const getAllTrainingCountries = (userId: string): Promise<string[]> => 
  locationService.getAllTrainingCountries(userId);